import { useEffect, useRef } from 'react';

export interface CardMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
}

// Ein Popover fuer alle Direktauswahlen am Board (Statusziele, Bearbeiter, Rueckfrage beim
// geteilten Endzustand). Bewusst inhaltsfrei: es kennt nur Eintraege und onClose, damit die drei
// Verwendungen kein eigenes Escape-/Fokus-Verhalten entwickeln (Lehre aus #52).
export function CardMenu({
  label,
  items,
  onClose,
}: {
  label: string;
  items: CardMenuItem[];
  onClose: () => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  // Der Knopf, der das Menue geoeffnet hat. Beim Schliessen bekommt er den Fokus zurueck —
  // sonst faellt er auf <body> und der naechste Tab beginnt von vorn.
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    openerRef.current = document.activeElement as HTMLElement | null;
    listRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const opener = openerRef.current;
    return () => opener?.focus();
  }, []);

  useEffect(() => {
    // Capture-Phase + stopPropagation: das SidePanel haengt seinen Escape-Listener an `document`
    // (side-panel.tsx). Ohne das schliesst ein Escape im Menue zusaetzlich das Panel dahinter —
    // dieselbe Falle wie bei der Lightbox in Paket B.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
      if (buttons.length === 0) return;
      e.preventDefault();
      // Pfeiltasten gehoeren hier der Menue-Navigation — stopPropagation verhindert, dass
      // ein Listener dahinter (z. B. Listen-/Bild-Navigation) dieselbe Taste zusaetzlich verarbeitet.
      e.stopPropagation();
      const at = buttons.findIndex((b) => b === document.activeElement);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = (at + step + buttons.length) % buttons.length;
      buttons[next]?.focus();
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (listRef.current?.contains(target)) return;
      // mousedown feuert vor click: ein Klick auf den Ausloeser-Knopf wuerde hier zuerst
      // schliessen, bevor dessen eigener onClick-Umschalter (offen<->zu) den alten, jetzt
      // veralteten Zustand sieht und das Menue direkt wieder oeffnet. Deshalb den Ausloeser
      // von diesem Handler ausnehmen — das Schliessen ueber ihn uebernimmt der Aufrufer selbst.
      // Ausnahme auf <body> ausschliessen: ohne fokussierten Ausloeser waere das die
      // Fallback-Wurzel und wuerde ansonsten jeden Aussenklick verschlucken.
      const opener = openerRef.current;
      if (opener && opener !== document.body && opener.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  return (
    <ul
      ref={listRef}
      role="menu"
      aria-label={label}
      className="absolute right-0 z-20 mt-1 max-h-56 w-48 overflow-auto rounded-btn border border-[var(--border)] bg-bg-surface text-sm shadow-md"
    >
      {items.map((item) => (
        <li key={item.key} role="presentation">
          <button
            type="button"
            role="menuitem"
            // Eine Optionsliste gehoert nicht in die Tab-Reihenfolge (Lehre aus dem
            // #47-Nutzertest: sonst bestimmt der Browser einen Eintrag als Tab-Ziel, waehrend das
            // Menue schliesst und das Ziel aus dem DOM reisst — der Fokus landet auf <body>).
            tabIndex={-1}
            onClick={() => {
              item.onSelect();
              onClose();
            }}
            className="block w-full px-2 py-1.5 text-left text-text-body hover:bg-brand-accent/10"
          >
            {item.label}
          </button>
        </li>
      ))}
    </ul>
  );
}
